// Read-only device + SIM identity discovery over an ADB shell (browser WebADB or anything that can
// run `adb shell <cmd>`). Generic discovery strategies, never model conditionals: every value
// is validated, and each value records the mechanism that produced it.
//
// Production source of truth for the USB Installer (physically validated in tools/webusb-adb-poc,
// which imports this module). Pure TypeScript without package imports, so the same code runs in
// the browser, in unit tests and in the PoC's native-adb harness.

export type Shell = (cmd: string) => Promise<string>;
export type Kind = 'DEVICE' | 'SIM' | 'TRANSPORT';
export type Reliability = 'HIGH' | 'CONDITIONAL' | 'SESSION/TRANSPORT';
export type Status = 'ok' | 'unavailable' | 'redacted' | 'denied' | 'invalid';

export interface Finding {
  kind: Kind;
  label: string;
  value: string | null;
  status: Status;
  source: string;
  reliability: Reliability;
  sensitive: boolean;
  note?: string;
}

// ---- validators ---------------------------------------------------------------------------------
export function luhn(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(digits.length - 1 - i) - 48;
    if (i % 2) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}
const allSame = (s: string) => /^(.)\1*$/.test(s);
export const isImei = (v: string) => /^\d{15}$/.test(v) && !allSame(v) && luhn(v);
/** ITU-T E.118: 89 + up to 20 digits incl. Luhn check digit; a trailing F pad is stripped first. */
export const normalizeIccid = (v: string) => v.trim().replace(/[Ff]+$/, '');
export const isIccid = (v: string) => /^89\d{16,18}$/.test(v) && luhn(v);
export const isImsi = (v: string) => /^\d{14,15}$/.test(v) && !allSame(v);
export const isRedacted = (v: string) => /\*|\[\*+\]|^x+$/i.test(v);
const isPlaceholderSerial = (v: string) => !v || /^(unknown|0123456789abcdef|0+)$/i.test(v);

export function mask(v: string | null): string {
  if (!v) return '';
  if (v.length <= 6) return '*'.repeat(v.length);
  return `${v.slice(0, 2)}${'*'.repeat(v.length - 5)}${v.slice(-3)}`;
}

// ---- parsers (defensive: output differs per Android version / OEM) -------------------------------
/** Decodes the reply of `service call` holding a String16: status word, length, UTF-16LE chars. */
export interface ParcelString { ok: boolean; value: string | null; error: string }
export function parseParcelString(out: string): ParcelString {
  // Drop the "0x00000000:" offsets, keep the 32-bit hex words in order (multi- and single-line forms).
  const words = out.replace(/0x[0-9a-f]+:/gi, ' ').replace(/'[^']*'/g, ' ').match(/\b[0-9a-f]{8}\b/gi) ?? [];
  if (!/Result: Parcel/.test(out) || words.length < 2) return { ok: false, value: null, error: out.trim().split('\n')[0] || 'no reply' };
  const bytes = new Uint8Array(words.length * 4);
  words.forEach((w, i) => { const n = parseInt(w, 16); bytes[i * 4] = n & 0xff; bytes[i * 4 + 1] = (n >>> 8) & 0xff; bytes[i * 4 + 2] = (n >>> 16) & 0xff; bytes[i * 4 + 3] = n >>> 24; });
  const view = new DataView(bytes.buffer);
  const status = view.getInt32(0, true);
  const len = view.getInt32(4, true);
  const str = (count: number) => new TextDecoder('utf-16le').decode(bytes.subarray(8, 8 + Math.max(0, count) * 2));
  if (status !== 0) return { ok: false, value: null, error: `exception ${status}: ${len > 0 ? str(len) : ''}`.trim() };
  if (len < 0) return { ok: true, value: null, error: '' };
  return { ok: true, value: str(len), error: '' };
}

/** Parses `content query` rows: "Row: 0 _id=1, icc_id=..., sim_id=0, ...". */
export function parseContentRows(out: string): Record<string, string>[] {
  return out.split('\n').filter((l) => /^Row: \d+ /.test(l)).map((l) => {
    const body = l.replace(/^Row: \d+ /, '');
    const row: Record<string, string> = {};
    for (const part of body.split(/, (?=[A-Za-z_][A-Za-z0-9_]*=)/)) {
      const i = part.indexOf('=');
      if (i > 0) row[part.slice(0, i)] = part.slice(i + 1);
    }
    return row;
  });
}

/** Parses `getprop` output into a map. */
export function parseGetprop(out: string): Map<string, string> {
  const m = new Map<string, string>();
  for (const line of out.split('\n')) {
    const r = line.match(/^\[([^\]]+)\]: \[(.*)\]\s*$/);
    if (r) m.set(r[1], r[2]);
  }
  return m;
}

// ---- IPhoneSubInfo transaction codes (AOSP IPhoneSubInfo.aidl, by API level) ---------------------
// All codes used here are read-only getters in every listed version, and every reply is validated,
// so a wrong code can only yield "invalid", never a side effect.
interface SubInfoCodes { deviceIdForPhone: (slot: number) => string; subscriberId: string; iccSerial: string; verified: string }
export function subInfoCodes(sdk: number): SubInfoCodes | null {
  const pkg = 's16 com.android.shell';
  if (sdk >= 28 && sdk <= 29) return {
    deviceIdForPhone: (slot) => `3 i32 ${slot} ${pkg}`, subscriberId: `7 ${pkg}`, iccSerial: `11 ${pkg}`,
    verified: 'hardware-verified on API 28 and 29',
  };
  if (sdk >= 30 && sdk <= 33) return {
    deviceIdForPhone: (slot) => `4 i32 ${slot} ${pkg} s16 ptt-usb-installer`, subscriberId: `8 ${pkg}`, iccSerial: `13 ${pkg}`,
    verified: sdk === 33 ? 'hardware-verified on API 33' : 'AOSP table, not hardware-verified on this API level',
  };
  return null;
}

// ---- discovery ----------------------------------------------------------------------------------
export interface UsbInfo { vendorId: number; productId: number; serialNumber?: string | null; productName?: string | null; manufacturerName?: string | null }

/** Structured identity sent to the backend (full values; the UI masks them). */
export interface SimIdentity { slot: number; iccid: string; imsi: string; operatorNumeric: string; operatorName: string }
export interface RadioIdentity {
  manufacturer: string; brand: string; model: string; device: string;
  androidRelease: string; sdkInt: number; buildFingerprint: string; abiList: string;
  serial: string; imeis: string[]; installKey: string; sims: SimIdentity[];
}
export interface Discovery { findings: Finding[]; identity: RadioIdentity }

export async function discoverIdentity(sh: Shell, adbSerial: string, usb?: UsbInfo): Promise<Discovery> {
  const f: Finding[] = [];
  const add = (x: Finding) => f.push(x);
  const props = parseGetprop(await sh('getprop'));
  const p = (k: string) => (props.get(k) ?? '').trim();
  const sdk = parseInt(p('ro.build.version.sdk'), 10) || 0;

  // DEVICE: build identity (not unique per unit; used for Model Catalog matching)
  for (const [label, key] of [['Manufacturer', 'ro.product.manufacturer'], ['Brand', 'ro.product.brand'], ['Model', 'ro.product.model'],
    ['Device', 'ro.product.device'], ['Board', 'ro.product.board'], ['Android release', 'ro.build.version.release'],
    ['SDK', 'ro.build.version.sdk'], ['Build fingerprint', 'ro.build.fingerprint'], ['ABI list', 'ro.product.cpu.abilist']] as const) {
    add({ kind: 'DEVICE', label, value: p(key) || null, status: p(key) ? 'ok' : 'unavailable', source: `getprop ${key}`, reliability: 'HIGH', sensitive: false, note: 'model/build identity, not unit-unique' });
  }
  // DEVICE: unit serials
  for (const key of ['ro.serialno', 'ro.boot.serialno']) {
    const v = p(key);
    add({ kind: 'DEVICE', label: key, value: isPlaceholderSerial(v) ? null : v, status: isPlaceholderSerial(v) ? (v ? 'invalid' : 'unavailable') : 'ok', source: `getprop ${key}`, reliability: 'HIGH', sensitive: false });
  }
  // DEVICE: Android ID as seen by the shell (= the system-wide value; an app sees its own per-key SSAID on 8+)
  const androidId = (await sh('settings get secure android_id')).trim();
  add({ kind: 'DEVICE', label: 'Android ID (settings secure)', value: /^[0-9a-f]{8,}$/i.test(androidId) ? androidId : null,
    status: /^[0-9a-f]{8,}$/i.test(androidId) ? 'ok' : androidId ? 'invalid' : 'unavailable', source: 'settings get secure android_id', reliability: 'CONDITIONAL', sensitive: true,
    note: 'changes on factory reset; equals the app value only for platform/system-UID apps (Android 8+ scopes it per signing key)' });

  // DEVICE: IMEI per modem slot
  const slotCount = (() => {
    const cfg = p('persist.radio.multisim.config').toLowerCase();
    if (cfg === 'dsds' || cfg === 'dsda') return 2;
    if (cfg === 'tsts') return 3;
    const states = p('gsm.sim.state');
    return states ? states.split(',').length : 1;
  })();
  const codes = subInfoCodes(sdk);
  // Generic getprop scan: any property named like *imei* holding a valid IMEI (OEMs expose these
  // under different names; this is a name-pattern strategy, not a per-model list).
  const imeiProps = [...props].filter(([k, v]) => /imei/i.test(k) && isImei(v.trim()));
  for (let slot = 0; slot < slotCount; slot++) {
    let value: string | null = null; let status: Status = 'unavailable'; const sources: string[] = []; let note = '';
    if (codes) {
      const r = parseParcelString(await sh(`service call iphonesubinfo ${codes.deviceIdForPhone(slot)}`));
      if (r.ok && r.value && isImei(r.value)) { value = r.value; status = 'ok'; sources.push(`iphonesubinfo getDeviceIdForPhone(${slot}) [${codes.verified}]`); }
      else if (!r.ok && /security|permission/i.test(r.error)) { status = 'denied'; note = r.error; }
      else if (r.ok && r.value) { status = isRedacted(r.value) ? 'redacted' : 'invalid'; note = `binder returned a non-IMEI value (len ${r.value.length})`; }
    } else note = `no IPhoneSubInfo code table for API ${sdk}`;
    // getprop candidates: name ends in the 1-based slot number (imei1/imei2) - a generic convention.
    const propHits = imeiProps.filter(([k]) => new RegExp(`imei_?${slot + 1}$`, 'i').test(k));
    for (const [k, v] of propHits) {
      if (!value) { value = v.trim(); status = 'ok'; }
      sources.push(v.trim() === value ? `getprop ${k}` : `getprop ${k} (DISAGREES)`);
    }
    add({ kind: 'DEVICE', label: `IMEI slot ${slot + 1}`, value, status, source: sources.join(' + ') || '-', reliability: 'HIGH', sensitive: true, note: note || undefined });
  }

  // SIM: per-slot state
  const simStates = p('gsm.sim.state').split(',');
  const simOps = p('gsm.sim.operator.numeric').split(',');
  const simAlpha = p('gsm.sim.operator.alpha').split(',');
  add({ kind: 'SIM', label: 'SIM slots', value: String(slotCount), status: 'ok', source: 'getprop persist.radio.multisim.config / gsm.sim.state', reliability: 'HIGH', sensitive: false });
  for (let slot = 0; slot < slotCount; slot++) {
    add({ kind: 'SIM', label: `Slot ${slot + 1} state`, value: simStates[slot] || null, status: simStates[slot] ? 'ok' : 'unavailable', source: 'getprop gsm.sim.state', reliability: 'CONDITIONAL', sensitive: false });
  }

  // SIM: subscription records. siminfo keeps rows for SIMs seen in the past (sim_id = -1); only
  // rows with sim_id >= 0 are physically inserted now.
  const simOut = await sh('content query --uri content://telephony/siminfo');
  const rows = parseContentRows(simOut);
  const denied = /permission denial|securityexception/i.test(simOut);
  const present = rows.filter((r) => /^\d+$/.test(r.sim_id ?? '') && parseInt(r.sim_id, 10) >= 0);
  const history = rows.length - present.length;
  if (!rows.length) {
    add({ kind: 'SIM', label: 'Subscriptions', value: null, status: denied ? 'denied' : 'unavailable', source: 'content://telephony/siminfo', reliability: 'CONDITIONAL', sensitive: false, note: denied ? simOut.trim().split('\n')[0] : undefined });
  }
  for (const r of present) {
    const slot = parseInt(r.sim_id, 10) + 1;
    const iccid = normalizeIccid(r.icc_id ?? '');
    const mcc = r.mcc_string ?? r.mcc ?? ''; const mnc = r.mnc_string ?? r.mnc ?? '';
    add({ kind: 'SIM', label: `Slot ${slot} ICCID`, value: isIccid(iccid) ? iccid : null, status: isIccid(iccid) ? 'ok' : isRedacted(iccid) ? 'redacted' : iccid ? 'invalid' : 'unavailable',
      source: 'content://telephony/siminfo icc_id', reliability: 'CONDITIONAL', sensitive: true, note: 'belongs to the SIM, moves with it' });
    add({ kind: 'SIM', label: `Slot ${slot} subscription ID`, value: r._id ?? null, status: r._id ? 'ok' : 'unavailable', source: 'content://telephony/siminfo _id', reliability: 'SESSION/TRANSPORT', sensitive: false, note: 'local database row id, device-local' });
    add({ kind: 'SIM', label: `Slot ${slot} home network (MCC/MNC)`, value: mcc && mnc ? `${mcc}/${mnc}` : simOps[slot - 1] || null, status: (mcc && mnc) || simOps[slot - 1] ? 'ok' : 'unavailable', source: mcc ? 'siminfo mcc/mnc' : 'getprop gsm.sim.operator.numeric', reliability: 'CONDITIONAL', sensitive: false });
    add({ kind: 'SIM', label: `Slot ${slot} SIM operator`, value: r.display_name || simAlpha[slot - 1] || null, status: 'ok', source: 'siminfo display_name / getprop gsm.sim.operator.alpha', reliability: 'CONDITIONAL', sensitive: false });
    add({ kind: 'SIM', label: `Slot ${slot} serving network`, value: r.carrier_name || null, status: r.carrier_name ? 'ok' : 'unavailable', source: 'siminfo carrier_name', reliability: 'SESSION/TRANSPORT', sensitive: false, note: 'current radio network, changes with coverage/roaming' });
    const msisdn = (r.number ?? '').replace(/^NULL$/, '');
    add({ kind: 'SIM', label: `Slot ${slot} MSISDN`, value: msisdn || null, status: msisdn ? 'ok' : 'unavailable', source: 'siminfo number', reliability: 'CONDITIONAL', sensitive: true, note: msisdn ? undefined : 'not stored on the SIM / not provisioned' });
  }
  if (history > 0) add({ kind: 'SIM', label: 'Historical SIM records', value: String(history), status: 'ok', source: 'siminfo rows with sim_id = -1', reliability: 'SESSION/TRANSPORT', sensitive: false, note: 'SIMs seen earlier but not inserted now - must not be used as the current SIM' });

  // SIM: default-subscription ICCID/IMSI via binder, cross-checked against siminfo
  if (codes) {
    const icc = parseParcelString(await sh(`service call iphonesubinfo ${codes.iccSerial}`));
    const iccV = icc.ok && icc.value ? normalizeIccid(icc.value) : '';
    const agrees = present.some((r) => normalizeIccid(r.icc_id ?? '') === iccV);
    add({ kind: 'SIM', label: 'Default-sub ICCID (binder)', value: isIccid(iccV) ? iccV : null, status: isIccid(iccV) ? 'ok' : !icc.ok && /security|permission/i.test(icc.error) ? 'denied' : iccV ? 'invalid' : 'unavailable',
      source: `iphonesubinfo getIccSerialNumber [${codes.verified}]`, reliability: 'CONDITIONAL', sensitive: true, note: isIccid(iccV) ? (agrees ? 'agrees with siminfo' : 'DISAGREES with siminfo') : undefined });
    const imsi = parseParcelString(await sh(`service call iphonesubinfo ${codes.subscriberId}`));
    const imsiV = imsi.ok && imsi.value ? imsi.value.trim() : '';
    const home = present[0] ? `${present[0].mcc_string ?? present[0].mcc ?? ''}${(present[0].mnc_string ?? present[0].mnc ?? '').padStart(2, '0')}` : simOps[0] ?? '';
    add({ kind: 'SIM', label: 'Default-sub IMSI (binder)', value: isImsi(imsiV) ? imsiV : null, status: isImsi(imsiV) ? 'ok' : !imsi.ok && /security|permission/i.test(imsi.error) ? 'denied' : imsiV ? 'invalid' : 'unavailable',
      source: `iphonesubinfo getSubscriberId [${codes.verified}]`, reliability: 'CONDITIONAL', sensitive: true,
      note: isImsi(imsiV) ? (home && imsiV.startsWith(home) ? `prefix matches home network ${home}` : `prefix does NOT match home network ${home}`) : undefined });
  }
  // SIM: OEM getprop scan for *iccid* (name-pattern strategy; reported, never preferred over siminfo)
  for (const [k, v] of props) {
    if (!/iccid/i.test(k)) continue;
    const n = normalizeIccid(v);
    if (!isIccid(n)) continue;
    add({ kind: 'SIM', label: `ICCID (getprop ${k})`, value: n, status: 'ok', source: `getprop ${k}`, reliability: 'CONDITIONAL', sensitive: true,
      note: present.some((r) => normalizeIccid(r.icc_id ?? '') === n) ? 'agrees with siminfo' : 'not in present siminfo rows' });
  }

  // TRANSPORT
  add({ kind: 'TRANSPORT', label: 'ADB serial', value: adbSerial, status: 'ok', source: 'ADB banner / USB iSerial', reliability: 'SESSION/TRANSPORT', sensitive: false });
  if (usb) {
    add({ kind: 'TRANSPORT', label: 'USB VID:PID', value: `${usb.vendorId.toString(16).padStart(4, '0')}:${usb.productId.toString(16).padStart(4, '0')}`, status: 'ok', source: 'WebUSB device descriptor', reliability: 'SESSION/TRANSPORT', sensitive: false, note: 'chipset vendor, not unit-unique' });
    add({ kind: 'TRANSPORT', label: 'USB serial (iSerial)', value: usb.serialNumber ?? null, status: usb.serialNumber ? 'ok' : 'unavailable', source: 'WebUSB device descriptor', reliability: 'SESSION/TRANSPORT', sensitive: false });
    add({ kind: 'TRANSPORT', label: 'USB product string', value: usb.productName ?? null, status: usb.productName ? 'ok' : 'unavailable', source: 'WebUSB device descriptor', reliability: 'SESSION/TRANSPORT', sensitive: false });
  }
  return { findings: f, identity: structured(f) };
}

/** Builds the structured identity from validated findings (status 'ok' only). */
export function structured(f: Finding[]): RadioIdentity {
  const v = (label: string) => f.find((x) => x.label === label && x.status === 'ok')?.value ?? '';
  const slots = parseInt(v('SIM slots'), 10) || 0;
  const imeis: string[] = [];
  for (let s = 1; f.some((x) => x.label === `IMEI slot ${s}`); s++) imeis.push(v(`IMEI slot ${s}`));
  const sims: SimIdentity[] = [];
  for (let s = 1; s <= Math.max(slots, 4); s++) {
    const iccid = v(`Slot ${s} ICCID`);
    if (!iccid) continue;
    const mccmnc = v(`Slot ${s} home network (MCC/MNC)`).replace('/', '');
    sims.push({ slot: s, iccid, imsi: '', operatorNumeric: /^\d{5,6}$/.test(mccmnc) ? mccmnc : '', operatorName: v(`Slot ${s} SIM operator`) });
  }
  // The binder IMSI belongs to the default subscription: attach it to the SIM with the binder's
  // ICCID, or to the only inserted SIM.
  const imsi = v('Default-sub IMSI (binder)');
  const defaultIccid = v('Default-sub ICCID (binder)');
  const owner = sims.find((s) => s.iccid === defaultIccid) ?? (sims.length === 1 ? sims[0] : undefined);
  if (owner && imsi) owner.imsi = imsi;
  return {
    manufacturer: v('Manufacturer'), brand: v('Brand'), model: v('Model'), device: v('Device'),
    androidRelease: v('Android release'), sdkInt: parseInt(v('SDK'), 10) || 0, buildFingerprint: v('Build fingerprint'),
    abiList: v('ABI list'), serial: v('ro.serialno') || v('ro.boot.serialno'), imeis,
    installKey: v('Android ID (settings secure)').toLowerCase(), sims,
  };
}

/** Short SHA-256 fingerprint so values can be compared across tools without revealing them. */
export async function fingerprint(v: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v));
  return Array.from(new Uint8Array(d).slice(0, 6)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
